import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { MapPin, Phone, Mail, Clock, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import WhatsAppButton from '@/components/WhatsAppButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'Contact Us – RSD Accessories Pvt. Ltd.';
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Visit Us',
      details: 'B-08, Sector-57, Noida-201301, Uttar Pradesh, India'
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: '+91 9311211889',
      link: 'tel:9311211889'
    },
    {
      icon: Mail,
      title: 'Email Us',
      details: 'rsdaccessories@gmail.com',
      link: 'mailto:rsdaccessories@gmail.com'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: 'Monday - Saturday: 9:00 AM - 6:00 PM'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-neutral-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-black mb-6">
            Get in{' '}
            <span className="text-[#FF4D00]">Touch</span>
          </h1>
          <p className="text-xl md:text-2xl text-neutral-600 max-w-3xl mx-auto">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <div
                  key={index}
                  data-testid={`contact-info-${index}`}
                  className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-[#FF4D00] rounded-full mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{info.title}</h3>
                  {info.link ? (
                    <a
                      href={info.link}
                      className="text-neutral-600 hover:text-[#FF4D00] transition-colors"
                    >
                      {info.details}
                    </a>
                  ) : (
                    <p className="text-neutral-600">{info.details}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form */}
            <div>
              <h2 className="text-4xl font-bold mb-6">Send us a Message</h2>
              <p className="text-lg text-neutral-600 mb-8">
                Fill out the form below and our team will get back to you within 24 hours.
              </p>

              {submitted && (
                <div data-testid="success-message" className="mb-8 bg-green-50 border border-green-200 rounded-xl p-6 flex items-center gap-4">
                  <CheckCircle2 className="w-8 h-8 text-green-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-green-900 mb-1">Message Sent!</h3>
                    <p className="text-green-700">We've received your message and will respond soon.</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-sm font-semibold mb-2 block">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    data-testid="name-input"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="h-12 rounded-lg"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-sm font-semibold mb-2 block">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    data-testid="email-input"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="h-12 rounded-lg"
                    placeholder="Enter your email"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-sm font-semibold mb-2 block">
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    data-testid="phone-input"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="h-12 rounded-lg"
                    placeholder="Enter your phone number (optional)"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-sm font-semibold mb-2 block">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    data-testid="message-input"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    className="min-h-32 rounded-lg"
                    placeholder="How can we help you?"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="submit-button"
                  disabled={submitting}
                  className="w-full bg-[#FF4D00] hover:bg-[#E04400] text-white rounded-full py-6 text-lg font-bold disabled:opacity-50"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>

            {/* Map */}
            <div>
              <h2 className="text-4xl font-bold mb-6">Our Location</h2>
              <div className="rounded-2xl overflow-hidden shadow-lg h-[500px] bg-neutral-100">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3503.8584394850457!2d77.35942931508!3d28.56992598245!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce5f7a0000001%3A0x0!2sNoida%20Sector%2057!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="RSD Accessories Location"
                ></iframe>
              </div>
              <div className="mt-6 bg-neutral-50 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-2">Office Address</h3>
                <p className="text-neutral-700">
                  <strong>RSD Accessories Pvt Ltd</strong><br />
                  B-08, Sector-57<br />
                  Noida-201301<br />
                  Uttar Pradesh, India
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <WhatsAppButton />
    </div>
  );
};

export default Contact;